// eslint-disable-next-line @typescript-eslint/triple-slash-reference, spaced-comment
///<reference path= "../../../../../node_modules/react-froala-wysiwyg/lib/index.d.ts" />

import React, { Component } from 'react';
import 'froala-editor/js/plugins/lists.min';
import 'froala-editor/js/plugins/word_paste.min';
import 'froala-editor/js/plugins/align.min';
import 'froala-editor/js/plugins/font_size.min';
import 'froala-editor/js/plugins/font_family.min';
import 'froala-editor/js/plugins/link.min';
import 'froala-editor/js/plugins/special_characters.min';
import 'froala-editor/js/plugins/char_counter.min';
import 'froala-editor/js/languages/de';
import FroalaEditor from 'react-froala-wysiwyg';

import { defaultLanguageCode } from '@zimpel/microapps/Mailings/domainValues';
import './Editor.css';

const removeEmptyParagraphs = (html: string): string => {
  if (typeof html !== 'string') { return html; }
  return html.replace(/<p>(\s|&nbsp;|\n|<br>|)+<\/p>/ig, '');
};

// Currently we need only a subset of the supported froala langauges,
// as our customers only support en and de.
// https://froala.com/wysiwyg-editor/languages/
type LanguageCode = 'de' | 'en';

export type Props = {
  id: string;
  onChange: (text: string) => void;
  allowedHtmlTags?: string[];
  allowedHtmlAttrs?: string[];
  language?: LanguageCode;
  text?: string;
  hasError?: boolean;
  placeholderText?: string;
  linkButton?: boolean;
  subAndSuperScript?: boolean;
  specialCharacters?: boolean;
  showCharacterCount?: boolean;
  maximumCharacterCount?: number;
  editorHeight?: number;
  dataTest?: string;
  autoFocus?: boolean;
};

type State = {
  model: string;
  language: string;
  fontSize: string;
};

const fontSizes = ['10', '12', '13', '14', '16', '18', '24'];
const defaultFontSize = '14';

class Editor extends Component<Props, State> {
  // We can't use typescript default handling in the constructor as react would show a runtime warning that
  // you must always pass props
  // We also can't define the defaultProps outside of the component (Editor.defaultProps = {}) as TypeScript
  // would say that defaultProps is not defined.
  // That is why we disable the eslint rule.

  static defaultProps = {
    allowedHtmlTags: ['p', 'strong', 'ul', 'em', 'ol', 'li', 'br', 'span', 'sup', 'sub', 'a', 'hr', 'u'],
    allowedHtmlAttrs: ['style', 'href', 'target', 'id'], // id was added to prevent insertHR error
    subAndSuperScript: true,
    specialCharacters: true,
  };

  // The froala editor currently does not have a type (other than object) for the config. So any is okay.
  froalaConfig: Record<string, any> = {
    toolbarButtons: [
      'bold',
      'italic',
      'underline',
      'fontSize',
      'formatUL',
      'formatOL',
      'alignLeft',
      'alignCenter',
      'alignRight',
      'alignJustify',
      'insertHR',
    ],
    toolbarSticky: false,
    attribution: false,
    listAdvancedTypes: false,
    wordPasteModal: false,
    wordPasteKeepFormatting: false,
    htmlAllowedStyleProps: [
      'text-align',
      'font-size',
    ],
    pasteAllowedStyleProps: [
      'text-align',
      'font-size',
    ],
    pasteDeniedTags: [
      'iframe',
      'img',
      'audio',
      'video',
      'picture',
      'object',
    ],
    fontSize: fontSizes,
    fontSizeSelection: true,
    imagePaste: false,
    pluginsEnabled: [
      'lists',
      'wordPaste',
      'align',
      'fontSize',
    ],
    events: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'paste.afterCleanup': (clipboardHtml: string): string => removeEmptyParagraphs(clipboardHtml),
    },
    // We don't know if key or apiKey is the right config, so we provide both, see:
    // https://froala.com/wysiwyg-editor/docs/activation
    // https://froala.com/wysiwyg-editor/docs/options#apiKey
    // Key is copied from:
    // https://confluence.mecom.de/pages/viewpage.action?spaceKey=ITE&title=Froala+WYSIWYG+Editor
    key: 'SDB17hB8E7C5B3C3G4gKTRe2CG1PGe1DESAe1Kg1EBC1Pe2TKoF4I4B3A9A3B5A4B2D3D3==',
    apiKey: 'SDB17hB8E7C5B3C3G4gKTRe2CG1PGe1DESAe1Kg1EBC1Pe2TKoF4I4B3A9A3B5A4B2D3D3==',
  };

  constructor(props: Props) {
    super(props);
    const { text, language } = props;
    this.handleModelChange = this.handleModelChange.bind(this);
    this.state = {
      model: text || '',
      language: language || defaultLanguageCode,
      fontSize: defaultFontSize,
    };
    this.constructFroalaConfig();
  }

  handleModelChange = (model: string): void => {
    const { onChange } = this.props;
    this.setState({ model });
    onChange(model);
  };

  constructFroalaConfig = (): void => {
    const { language, fontSize } = this.state;
    const {
      allowedHtmlTags,
      allowedHtmlAttrs,
      autoFocus,
    } = this.props;
    let {
      linkButton,
      subAndSuperScript,
      specialCharacters,
      showCharacterCount,
      maximumCharacterCount,
      editorHeight,
      placeholderText,
    } = this.props;

    linkButton = typeof linkButton === 'undefined' ? true : linkButton;
    subAndSuperScript = typeof subAndSuperScript === 'undefined' ? false : subAndSuperScript;
    specialCharacters = typeof specialCharacters === 'undefined' ? false : specialCharacters;
    showCharacterCount = typeof showCharacterCount === 'undefined' ? false : showCharacterCount;
    maximumCharacterCount = typeof maximumCharacterCount === 'undefined' ? -1 : maximumCharacterCount;
    editorHeight = typeof editorHeight === 'undefined' ? 400 : editorHeight;
    placeholderText = typeof placeholderText === 'undefined' ? '' : placeholderText;

    this.froalaConfig.language = language;
    if (linkButton) { this.addLinkConfig(); }
    if (subAndSuperScript) { this.addSupSubConfig(); }
    if (specialCharacters) { this.addSpecialCharactersConfig(); }
    if (showCharacterCount || maximumCharacterCount > -1) {
      this.addCharacterCountConfig(maximumCharacterCount);
    }
    this.froalaConfig.htmlAllowedTags = allowedHtmlTags;
    this.froalaConfig.htmlAllowedAttrs = allowedHtmlAttrs;
    this.froalaConfig.fontSizeDefaultSelection = fontSize;
    this.froalaConfig.placeholderText = placeholderText;
    this.froalaConfig.heightMin = editorHeight;
    this.froalaConfig.heightMax = editorHeight;
    this.froalaConfig.autofocus = autoFocus;
  };

  addLinkConfig = (): void => {
    this.froalaConfig.pluginsEnabled.push('link');
    this.froalaConfig.toolbarButtons.push('insertLink');
    this.froalaConfig.linkEditButtons = ['linkOpen', 'linkEdit', 'linkRemove'];
    this.froalaConfig.linkInsertButtons = ['linkBack'];
    this.froalaConfig.linkAlwaysBlank = true;
  };

  addSupSubConfig = (): void => {
    this.froalaConfig.toolbarButtons.push('subscript');
    this.froalaConfig.toolbarButtons.push('superscript');
  };

  addSpecialCharactersConfig = (): void => {
    this.froalaConfig.pluginsEnabled.push('specialCharacters');
    this.froalaConfig.toolbarButtons.push('specialCharacters');
    this.froalaConfig.specialCharactersSets = [{
      title: 'Latin',
      char: '&trade;',
      list: [
        { char: '&trade;', desc: 'TRADEMARK SIGN' },
        { char: '&copy;', desc: 'COPYRIGHT SIGN' },
        { char: '&reg;', desc: 'REGISTERED SIGN' },
        { char: '&deg;', desc: 'DEGREE SIGN' },
        { char: '&sup1;', desc: 'SUPERSCRIPT ONE' },
        { char: '&sup2;', desc: 'SUPERSCRIPT TWO' },
        { char: '&sup3;', desc: 'SUPERSCRIPT THREE' },
      ],
    }];
  };

  addCharacterCountConfig = (maximumCharacterCount: number): void => {
    this.froalaConfig.pluginsEnabled.push('charCounter');
    this.froalaConfig.charCounterCount = true;
    this.froalaConfig.charCounterMax = maximumCharacterCount;
  };

  render(): React.ReactElement {
    const { model, fontSize } = this.state;
    const {
      id, hasError, dataTest,
    } = this.props;

    return (
      <div
        id={id}
        data-test={dataTest}
        className={hasError ? 'froala-editor-error' : ''}
        style={{ fontSize }}
      >
        <FroalaEditor
          config={this.froalaConfig}
          model={model}
          onModelChange={this.handleModelChange}
        />
      </div>
    );
  }
}

export default Editor;
export { removeEmptyParagraphs, fontSizes, defaultFontSize };
